import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FinancialDataProvider, useFinancialData } from './contexts/FinancialDataContext';
import { LandingPage, MARKETING_PATHS, OnboardingPage, DashboardPage, BetaGatePage, TermsGatePage } from './pages';
import { BETA_GATE_ENABLED, PAYMENTS_ENABLED, STORAGE_KEYS } from './config/constants';
import { createCheckoutSession } from './services/stripeService';
import { DashboardView } from './types';


// Inner component that uses the contexts
const AppContent: React.FC = () => {
  const { darkMode } = useTheme();
  const { appState, setAppState, setSheetId, sheetId, isSignedIn, isReady, handleSignIn, user, userProfile, paymentTier, googleTokenFailed, sheetsScopeMissing, reconnectGoogle, refreshGoogleToken } = useAuth();

  // Visitors who picked Pro on the pricing page go straight to Stripe once
  // they are signed in and past the terms screen (so onboarding and the
  // dashboard are the only states that qualify). If checkout can't be opened,
  // the dashboard shows the upgrade screen instead ('show').
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);
  const checkoutStartedRef = useRef(false);
  useEffect(() => {
    if (!PAYMENTS_ENABLED || checkoutStartedRef.current) return;
    if (!userProfile || !user?.email) return;
    if (appState !== 'onboarding' && appState !== 'dashboard') return;

    let intent: string | null = null;
    try {
      intent = localStorage.getItem(STORAGE_KEYS.UPGRADE_INTENT);
    } catch {
      return;
    }
    if (intent !== 'launch' && intent !== 'regular') return;
    checkoutStartedRef.current = true;

    if (paymentTier === 'pro') {
      localStorage.removeItem(STORAGE_KEYS.UPGRADE_INTENT);
      return;
    }

    setRedirectingToCheckout(true);
    createCheckoutSession(user.email, intent === 'launch')
      .then((url) => {
        localStorage.removeItem(STORAGE_KEYS.UPGRADE_INTENT);
        window.location.href = url;
      })
      .catch((err) => {
        console.error('Could not open checkout after sign-in:', err);
        try {
          localStorage.setItem(STORAGE_KEYS.UPGRADE_INTENT, 'show');
        } catch {
          /* no fallback prompt without storage */
        }
        setRedirectingToCheckout(false);
      });
  }, [appState, userProfile, user?.email, paymentTier]);

  // Gate sign-in behind beta code — if already approved (localStorage), go straight to OAuth
  const handleSignInWithBetaCheck = useCallback(() => {
    if (!BETA_GATE_ENABLED || localStorage.getItem(STORAGE_KEYS.BETA_APPROVED) === 'true') {
      handleSignIn();
    } else {
      setAppState('beta-gate');
    }
  }, [handleSignIn, setAppState]);
  const { loadDataFromSheets, createNewSheet, updateDashboardView } = useFinancialData();
  const [hasLoadedData, setHasLoadedData] = useState(false);
  // Start with loading overlay if returning user or mid-OAuth redirect (but not beta-gate)
  const [isInitialLoading, setIsInitialLoading] = useState(() => appState === 'dashboard' || appState === 'loading');
  const isLoadingRef = useRef(false);
  const isReadyRef = useRef(isReady);
  isReadyRef.current = isReady;
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Debounce googleTokenFailed so a brief GIS timeout that self-recovers
  // doesn't flash the reconnect screen (stale tab scenario)
  const [showReconnect, setShowReconnect] = useState(false);
  useEffect(() => {
    if (googleTokenFailed && !isReady) {
      const timer = setTimeout(() => setShowReconnect(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setShowReconnect(false);
    }
  }, [googleTokenFailed, isReady]);

  // Load data when user signs in with an existing sheet
  useEffect(() => {
    const loadData = async () => {
      if (isReady && isSignedIn && sheetId && appState === 'dashboard' && !hasLoadedData && !isLoadingRef.current) {
        isLoadingRef.current = true;
        setIsInitialLoading(true);
        try {
          const result = await loadDataFromSheets(sheetId);
          if (result.recreated && result.newSheetId) {
            setSheetId(result.newSheetId);
          }
          setHasLoadedData(true);
          setLoadFailed(false);
        } catch (err) {
          // Check if this is an auth error (401/403) vs a transient server error (500)
          const errStatus = (err as { status?: number })?.status
            || (err as { result?: { error?: { code?: number } } })?.result?.error?.code;
          if (errStatus === 401 || errStatus === 403) {
            console.error('Failed to load data (auth error), refreshing token and retrying...', err);
            await refreshGoogleToken();
          } else {
            console.error('Failed to load data (server error), retrying...', err);
          }
          await new Promise(r => setTimeout(r, 2000));
          try {
            const result = await loadDataFromSheets(sheetId);
            if (result.recreated && result.newSheetId) {
              setSheetId(result.newSheetId);
            }
            setHasLoadedData(true);
            setLoadFailed(false);
          } catch (retryErr) {
            console.error('Retry also failed:', retryErr);
            setLoadFailed(true);
          }
        } finally {
          isLoadingRef.current = false;
          setIsInitialLoading(false);
        }
      }
    };

    loadData();
  }, [isReady, isSignedIn, sheetId, appState, hasLoadedData, loadDataFromSheets, setSheetId, refreshGoogleToken]);

  // Sync URL with appState — show /dashboard when on the dashboard, and keep
  // public marketing URLs (/features, /pricing, /login) while on the landing page
  useEffect(() => {
    const current = window.location.pathname;
    const path = appState === 'dashboard'
      ? '/dashboard'
      : appState === 'landing' && MARKETING_PATHS[current] ? current : '/';
    if (window.location.pathname !== path) {
      window.history.replaceState(null, '', path);
    }
    // /dashboard is served from index.html (or from /login or /pricing when
    // signing in there), so the tab kept that page's title. The marketing
    // pages keep the titles scripts/prerender.mjs gives them.
    if (appState === 'dashboard') document.title = 'TallyUp | Dashboard';
    else if (appState === 'onboarding') document.title = 'TallyUp | Get Started';
    else if (appState !== 'landing') document.title = 'TallyUp';
  }, [appState]);

  // Reset hasLoadedData when user signs out or sheet changes
  useEffect(() => {
    if (!isSignedIn) {
      setHasLoadedData(false);
    }
  }, [isSignedIn]);
  useEffect(() => {
    setHasLoadedData(false);
    setLoadFailed(false);
  }, [sheetId]);

  // Handle onboarding sheet creation
  const handleCreateSheet = useCallback(async () => {
    // Wait up to 15s for Google API to be ready (token + gapi loaded)
    for (let i = 0; i < 15; i++) {
      if (isReadyRef.current) break;
      await new Promise(r => setTimeout(r, 1000));
    }
    if (!isReadyRef.current) {
      // Token not available — trigger reconnect and wait again
      reconnectGoogle();
      // Wait up to 15s more after reconnect
      for (let i = 0; i < 15; i++) {
        if (isReadyRef.current) break;
        await new Promise(r => setTimeout(r, 1000));
      }
      if (!isReadyRef.current) {
        throw new Error('Google connection timed out. Please try again.');
      }
    }
    try {
      const newSheetId = await createNewSheet();
      setSheetId(newSheetId);
      return newSheetId;
    } catch (err) {
      // If sheet creation fails with auth error, try reconnect + retry once
      const status = (err as { status?: number })?.status || (err as { result?: { error?: { code?: number } } })?.result?.error?.code;
      if (status === 401 || status === 403) {
        reconnectGoogle();
        throw new Error('Session expired. Reconnecting — please try again.');
      }
      throw err;
    }
  }, [createNewSheet, setSheetId, reconnectGoogle]);

  // Clean stale # from URL (left by Supabase OAuth redirect)
  useEffect(() => {
    if (window.location.href.includes('#')) {
      if (isSignedIn || !window.location.hash.includes('access_token')) {
        window.history.replaceState(null, '', window.location.origin + window.location.pathname);
      }
    }
  }, [isSignedIn]);

  // Loading timeout bailout — only starts AFTER auth is ready (isSignedIn),
  // so slow auth restore on a valid session doesn't falsely trigger "session expired".
  const isWaitingForData = isSignedIn && isInitialLoading && appState === 'dashboard';
  const isWaitingForAuth = !isSignedIn && appState === 'dashboard';
  useEffect(() => {
    if (!isWaitingForData && !isWaitingForAuth) {
      setLoadingTimedOut(false);
      return;
    }
    // Auth restore gets 15s (includes token acquisition), data load gets 10s
    const timeout = isWaitingForAuth ? 15000 : 10000;
    const timer = setTimeout(() => setLoadingTimedOut(true), timeout);
    return () => clearTimeout(timer);
  }, [isWaitingForData, isWaitingForAuth]);

  // Reset loading timeout when Google token is freshly acquired (e.g. after OAuth redirect)
  // so the "Taking longer" screen doesn't flash while data loads with the new token.
  useEffect(() => {
    if (isReady && loadingTimedOut) {
      setLoadingTimedOut(false);
    }
  }, [isReady, loadingTimedOut]);

  // Note: we no longer auto-recover on timeout — auto-firing reconnectGoogle()
  // without a user gesture causes GIS popup to be blocked, leading to a redirect
  // loop. Instead we just show the "Taking longer than expected" UI and let the
  // user click Reconnect (which IS a user gesture, so GIS popup works).

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback(async (selectedView: DashboardView) => {
    const storedSheetId = sheetId || localStorage.getItem('tallyup_sheet_id');
    if (storedSheetId) {
      try {
        // Save the selected dashboard view
        if (selectedView !== 'standard') {
          await updateDashboardView(selectedView, storedSheetId);
        }
        const result = await loadDataFromSheets(storedSheetId);
        if (result.recreated && result.newSheetId) {
          setSheetId(result.newSheetId);
        }
        setHasLoadedData(true);
      } catch (err) {
        console.error('Failed to load data after onboarding:', err);
        // Still go to dashboard — the data load effect will retry
      }
    }
    setIsInitialLoading(false);
    setAppState('dashboard');
  }, [sheetId, loadDataFromSheets, setAppState, setSheetId, updateDashboardView]);

  if (redirectingToCheckout) {
    return (
      <div className={`min-h-[100dvh] w-full flex flex-col items-center justify-center gap-4 ${
        darkMode ? 'bg-slate-950 text-slate-300' : 'bg-[#f8f9ff] text-slate-600'
      }`}>
        <Loader2 size={32} className="animate-spin text-violet-500" />
        <p className="text-base">Taking you to secure checkout…</p>
      </div>
    );
  }

  // Render landing page
  if (appState === 'landing') {
    return <LandingPage onSignIn={handleSignInWithBetaCheck} page={MARKETING_PATHS[window.location.pathname] ?? 'home'} />;
  }

  // Render beta gate
  if (appState === 'beta-gate') {
    return <BetaGatePage />;
  }

  // Render terms acceptance gate
  if (appState === 'terms-gate') {
    return <TermsGatePage />;
  }

  // Render onboarding
  if (appState === 'onboarding') {
    return (
      <OnboardingPage
        onCreateSheet={handleCreateSheet}
        onComplete={handleOnboardingComplete}
        userName={user?.user_metadata?.full_name?.split(' ')[0] || 'there'}
      />
    );
  }

  // Show scope-missing screen if user didn't grant Sheets permission
  if (isSignedIn && sheetsScopeMissing) {
    return (
      <div className={`min-h-[100dvh] w-full flex items-center justify-center ${
        darkMode ? 'bg-slate-950' : 'bg-[#f8f9ff]'
      }`}>
        <div className="text-center space-y-4 px-6 max-w-md">
          <p className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Google Sheets access required
          </p>
          <p className={`text-base ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            TallyUp Finance needs permission to read and write Google Sheets to save your financial data. Please sign in again and make sure the Google Sheets checkbox is selected.
          </p>
          <button
            onClick={reconnectGoogle}
            className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
          >
            Grant Sheets Access
          </button>
        </div>
      </div>
    );
  }

  // Show reconnect screen if Google token acquisition failed (expired session, blocked cookies, etc.)
  // Uses debounced showReconnect to avoid flashing on stale tab re-focus
  if (isSignedIn && showReconnect) {
    return (
      <div className={`min-h-[100dvh] w-full flex items-center justify-center ${
        darkMode ? 'bg-slate-950' : 'bg-[#f8f9ff]'
      }`}>
        <div className="text-center space-y-4 px-6 max-w-sm">
          <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Google Sheets connection expired
          </p>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            We couldn't refresh your connection automatically. Tap below to reconnect.
          </p>
          <button
            onClick={reconnectGoogle}
            className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
          >
            Reconnect to Google Sheets
          </button>
        </div>
      </div>
    );
  }

  // Show loading spinner while auth restores or data loads (lightweight — doesn't render dashboard)
  if (!isSignedIn || isInitialLoading) {
    if (loadingTimedOut) {
      return (
        <div className={`min-h-[100dvh] w-full flex items-center justify-center ${
          darkMode ? 'bg-slate-950' : 'bg-[#f8f9ff]'
        }`}>
          <div className="text-center space-y-4 px-6 max-w-sm">
            <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Taking longer than expected
            </p>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Your session may have expired. Tap below to reconnect.
            </p>
            <button
              onClick={reconnectGoogle}
              className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              <RefreshCw size={16} />
              Reconnect
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={`min-h-[100dvh] w-full flex items-center justify-center ${
        darkMode ? 'bg-slate-950' : 'bg-[#f8f9ff]'
      }`}>
        <Loader2 size={40} className={`animate-spin ${darkMode ? 'text-violet-400' : 'text-violet-600'}`} />
      </div>
    );
  }

  // Show error screen if data load failed (instead of empty dashboard with zeros)
  if (loadFailed && !hasLoadedData) {
    return (
      <div className={`min-h-[100dvh] w-full flex items-center justify-center ${
        darkMode ? 'bg-slate-950' : 'bg-[#f8f9ff]'
      }`}>
        <div className="text-center space-y-4 px-6 max-w-sm">
          <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Failed to load your data
          </p>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Your Google Sheets connection may have expired. Tap below to reconnect and try again.
          </p>
          <button
            onClick={() => {
              setLoadFailed(false);
              setHasLoadedData(false);
              reconnectGoogle();
            }}
            className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2"
          >
            <RefreshCw size={16} />
            Reconnect & Retry
          </button>
        </div>
      </div>
    );
  }

  // Render dashboard
  return <DashboardPage />;
};

// Wrapper component to handle auth callback for data loading
const AppWithAuth: React.FC = () => {
  return (
    <FinancialDataProvider>
      <AppContent />
    </FinancialDataProvider>
  );
};

// Main App component with all providers
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppWithAuth />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
